import React from "react";
import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";
import { COLORS, FONTS } from "./constants";

const TABS = [
  "Chat",
  "Draft Email",
  "Research",
  "Battle Card",
  "Meeting Prep",
  "Sequence",
] as const;

/** Map sub-scene index (0-4) to active tab */
const ACTIVE_TAB_INDEX = [1, 2, 3, 4, 5];

const SUB_SCENE_DURATION = 270;

// --- Sub-scene content ---

const DraftEmailContent: React.FC<{ opacity: number }> = ({ opacity }) => (
  <div style={{ opacity }}>
    <div style={{ marginBottom: 20 }}>
      <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted }}>
        Input:{" "}
      </span>
      <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textSecondary }}>
        &quot;Draft a follow-up to Acme Corp about the Q2 proposal&quot;
      </span>
    </div>
    <div
      style={{
        background: COLORS.bg,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 12,
        padding: 24,
      }}
    >
      <div
        style={{
          fontFamily: FONTS.heading,
          fontSize: 16,
          fontWeight: 700,
          color: COLORS.accentBlue,
          marginBottom: 16,
        }}
      >
        Subject: Q2 Partnership Proposal — Next Steps
      </div>
      <div
        style={{
          fontFamily: FONTS.body,
          fontSize: 15,
          color: COLORS.textPrimary,
          lineHeight: 1.8,
        }}
      >
        Hi David,
        <br />
        <br />
        Following up on our conversation about the Q2 proposal. Based on our internal
        analysis
        <sup style={{ color: COLORS.accentGreen, fontSize: 10 }}>&sup1;</sup>, we believe
        the timeline aligns well with your Q3 planning cycle...
        <br />
        <br />
        Best regards,
        <br />
        [Your Name]
      </div>
    </div>
  </div>
);

const ResearchContent: React.FC<{ frame: number; offset: number }> = ({
  frame,
  offset,
}) => {
  const sections = [
    {
      title: "Key Facts",
      body: "Fortune 500, 12,000 employees, HQ: San Francisco",
    },
    {
      title: "Relationship History",
      body: "3 meetings in Q1, active proposal pending",
    },
    {
      title: "Opportunities",
      body: "Expanding APAC team, need knowledge management",
    },
    {
      title: "Recommended Approach",
      body: "Lead with ROI data from similar-sized deploys",
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted }}>
          Input:{" "}
        </span>
        <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textSecondary }}>
          &quot;Research Acme Corp before our meeting next week&quot;
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {sections.map((sec, i) => {
          const secOpacity = interpolate(
            frame,
            [offset + 30 + i * 15, offset + 55 + i * 15],
            [0, 1],
            { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
          );
          return (
            <div key={sec.title} style={{ opacity: secOpacity }}>
              <div
                style={{
                  fontFamily: FONTS.heading,
                  fontSize: 15,
                  fontWeight: 700,
                  color: COLORS.accentPurple,
                  marginBottom: 4,
                }}
              >
                {sec.title}
              </div>
              <div
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 14,
                  color: COLORS.textSecondary,
                  lineHeight: 1.6,
                }}
              >
                {sec.body}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const BattleCardContent: React.FC<{ opacity: number }> = ({ opacity }) => {
  const rows = [
    {
      feature: "Multi-source",
      us: "\u2705 6 integrations",
      them: "\u26A0\uFE0F 2 only",
    },
    {
      feature: "Citations",
      us: "\u2705 Every answer",
      them: "\u274C None",
    },
    {
      feature: "Pricing",
      us: "\u2705 From \u20B93K/mo",
      them: "\u274C $15/seat/mo",
    },
    {
      feature: "Privacy",
      us: "\u2705 Tenant isolated",
      them: "\u26A0\uFE0F Shared infra",
    },
  ];

  return (
    <div style={{ opacity }}>
      <div style={{ marginBottom: 20 }}>
        <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted }}>
          Input:{" "}
        </span>
        <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textSecondary }}>
          &quot;Compare us vs CompetitorX for enterprise deals&quot;
        </span>
      </div>
      <div
        style={{
          background: COLORS.bg,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        {/* Table header */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            padding: "12px 20px",
            borderBottom: `1px solid ${COLORS.border}`,
            fontFamily: FONTS.mono,
            fontSize: 13,
            fontWeight: 700,
            color: COLORS.textMuted,
          }}
        >
          <span>Feature</span>
          <span>Us</span>
          <span>CompetitorX</span>
        </div>
        {/* Table rows */}
        {rows.map((row, i) => (
          <div
            key={row.feature}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              padding: "14px 20px",
              borderBottom:
                i < rows.length - 1 ? `1px solid ${COLORS.border}` : "none",
              fontFamily: FONTS.body,
              fontSize: 14,
            }}
          >
            <span style={{ color: COLORS.textPrimary, fontWeight: 600 }}>
              {row.feature}
            </span>
            <span style={{ color: COLORS.accentGreen }}>{row.us}</span>
            <span style={{ color: COLORS.textMuted }}>{row.them}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const MeetingPrepContent: React.FC<{ frame: number; offset: number }> = ({
  frame,
  offset,
}) => {
  const sections = [
    {
      icon: "\uD83D\uDCCB",
      title: "Background",
      body: "Q1 revenue up 23%, two new enterprise clients",
    },
    {
      icon: "\uD83D\uDCAC",
      title: "Talking Points",
      body: 'Emphasize customer retention rate (94%)',
    },
    {
      icon: "\u2753",
      title: "Questions to Expect",
      body: "Timeline for Series B, hiring plan",
    },
    {
      icon: "\u26A0\uFE0F",
      title: "Watch Out For",
      body: "CFO concerned about burn rate",
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted }}>
          Input:{" "}
        </span>
        <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textSecondary }}>
          &quot;Prepare me for the board meeting tomorrow&quot;
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {sections.map((sec, i) => {
          const secOpacity = interpolate(
            frame,
            [offset + 30 + i * 15, offset + 55 + i * 15],
            [0, 1],
            { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
          );
          return (
            <div
              key={sec.title}
              style={{
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
                opacity: secOpacity,
              }}
            >
              <span style={{ fontSize: 18 }}>{sec.icon}</span>
              <div>
                <div
                  style={{
                    fontFamily: FONTS.heading,
                    fontSize: 15,
                    fontWeight: 700,
                    color: COLORS.textPrimary,
                    marginBottom: 3,
                  }}
                >
                  {sec.title}
                </div>
                <div
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 14,
                    color: COLORS.textSecondary,
                    lineHeight: 1.5,
                  }}
                >
                  {sec.body}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SequenceContent: React.FC<{ frame: number; offset: number }> = ({
  frame,
  offset,
}) => {
  const emails = [
    {
      title: "Email 1: Introduction",
      day: "Day 1",
      preview: "Introduce EAKC's healthcare knowledge platform and request a brief call.",
    },
    {
      title: "Email 2: Value Proposition",
      day: "Day 3",
      preview: "Share case study: 40% reduction in onboarding time at MedTech Inc.",
    },
    {
      title: "Email 3: Call to Action",
      day: "Day 7",
      preview: "Offer a free 14-day pilot with their existing documentation stack.",
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted }}>
          Input:{" "}
        </span>
        <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textSecondary }}>
          &quot;Create 3-email outreach to healthcare leads&quot;
        </span>
      </div>
      <div style={{ display: "flex", gap: 16 }}>
        {emails.map((email, i) => {
          const cardOpacity = interpolate(
            frame,
            [offset + 30 + i * 20, offset + 60 + i * 20],
            [0, 1],
            { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
          );
          return (
            <div
              key={email.title}
              style={{
                flex: 1,
                background: COLORS.bg,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 12,
                padding: 18,
                opacity: cardOpacity,
              }}
            >
              <div
                style={{
                  fontFamily: FONTS.heading,
                  fontSize: 14,
                  fontWeight: 700,
                  color: COLORS.accentOrange,
                  marginBottom: 6,
                }}
              >
                {email.title}
              </div>
              <div
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 11,
                  color: COLORS.textMuted,
                  marginBottom: 10,
                }}
              >
                {email.day}
              </div>
              <div
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  color: COLORS.textSecondary,
                  lineHeight: 1.5,
                }}
              >
                {email.preview}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const Scene06Modes: React.FC = () => {
  const frame = useCurrentFrame();

  // Determine which sub-scene is active (0-4)
  const subScene = Math.min(4, Math.floor(frame / SUB_SCENE_DURATION));
  const subFrame = frame - subScene * SUB_SCENE_DURATION;

  // Content fade in/out per sub-scene
  const contentFadeIn = interpolate(subFrame, [0, 30], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const contentFadeOut = interpolate(subFrame, [240, 270], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  // Last sub-scene doesn't need to fade out to the next, just at end
  const contentOpacity =
    subScene === 4
      ? interpolate(subFrame, [0, 30, 240, 270], [0, 1, 1, 0], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        })
      : contentFadeIn * contentFadeOut;

  const activeTab = ACTIVE_TAB_INDEX[subScene];

  // Title / subtitle (persistent)
  const titleOpacity = interpolate(frame, [0, 25], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const subSceneOffset = subScene * SUB_SCENE_DURATION;

  return (
    <AbsoluteFill>
      <GridBackground />

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 60,
          left: 0,
          right: 0,
          textAlign: "center",
          fontFamily: FONTS.heading,
          fontSize: 36,
          fontWeight: 700,
          color: COLORS.textPrimary,
          opacity: titleOpacity,
        }}
      >
        6 Specialized Modes
      </div>

      {/* Subtitle */}
      <div
        style={{
          position: "absolute",
          top: 105,
          left: 0,
          right: 0,
          textAlign: "center",
          fontFamily: FONTS.body,
          fontSize: 16,
          color: COLORS.textSecondary,
          opacity: titleOpacity,
        }}
      >
        Not just a chatbot — tailored for how teams actually work
      </div>

      {/* Tabs row */}
      <div
        style={{
          position: "absolute",
          top: 160,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          gap: 16,
        }}
      >
        {TABS.map((tab, i) => {
          const isActive = i === activeTab;
          return (
            <div
              key={tab}
              style={{
                fontFamily: FONTS.mono,
                fontSize: 13,
                padding: "8px 20px",
                borderRadius: 20,
                background: isActive ? COLORS.accentBlue : COLORS.bgCard,
                color: isActive ? "#FFFFFF" : COLORS.textMuted,
                border: isActive ? "none" : `1px solid ${COLORS.border}`,
                transition: "all 0.3s",
              }}
            >
              {tab}
            </div>
          );
        })}
      </div>

      {/* Content card */}
      <div
        style={{
          position: "absolute",
          left: 360,
          top: 220,
          width: 1200,
          height: 550,
          background: COLORS.bgCard,
          border: `1px solid ${COLORS.borderLight}`,
          borderRadius: 16,
          padding: 30,
          overflow: "hidden",
        }}
      >
        <div style={{ opacity: contentOpacity }}>
          {subScene === 0 && <DraftEmailContent opacity={1} />}
          {subScene === 1 && (
            <ResearchContent frame={frame} offset={subSceneOffset} />
          )}
          {subScene === 2 && <BattleCardContent opacity={1} />}
          {subScene === 3 && (
            <MeetingPrepContent frame={frame} offset={subSceneOffset} />
          )}
          {subScene === 4 && (
            <SequenceContent frame={frame} offset={subSceneOffset} />
          )}
        </div>
      </div>

    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
